import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { 
  accessLocalStorage, 
  createIncomingConfig, 
  fetchEndpoints,
  exportSelectAllQueues,
  getContextsByTenant,
  getIntegrationsByAccountcode
} from '../../../services';
import { getAvailableTrunks } from '../../../services/incomingCallsApi';

import PbxContext from '../../../context/PbxContext';
import '../../../libs/bulma.min.css';
import './IncomingCallsForm.css';

import Loading from '../../../components/Loading/LoadingModule';
import TrunkSelector from './components/TrunkSelector';
import TrunkSelectorOutbound from './components/TrunkSelectorOutbound';
import AudioSelectorUploads from '../Audio/AudioSelectorUploads';
import MohConfiguration from '../shared/MohConfiguration';
import StasisFallbackInputs from '../shared/StasisFallbackInputs';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faArrowLeft, 
  faPhone, 
  faInfoCircle,
  faPhoneAlt,
  faMask,
  faServer,
  faRoute,
  faRecordVinyl,
  faVolumeUp,
  faClock,
  faToggleOn,
  faToggleOff,
  faPlus,
  faTrash,
  faExchangeAlt,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';

function IncomingCallsCreate() {
  const getItensStateGlobal = useContext(PbxContext);
  const { toggleIsChangeFormElements, toggleIsHidden } = getItensStateGlobal;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableEndpoints, setAvailableEndpoints] = useState([]);
  const [availableQueues, setAvailableQueues] = useState([]);
  const [availableContexts, setAvailableContexts] = useState([]);
  const [availableTrunks, setAvailableTrunks] = useState([]);
  const [hasWhatsAppIntegration, setHasWhatsAppIntegration] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    type: '',
    message: ''
  });
  const [userTenant, setUserTenant] = useState('');
  const [userRole, setUserRole] = useState('');

  // Estados do formulário
  const [values, setValues] = useState({
    tenant: '',
    mask: '',
    trunk_name: '',
    destination_type_main: 'endpoint',
    destination_main: '',
    // Campos do destino stasis (App voice-gateway-ai)
    stasis_fallback_type: '',
    stasis_fallback_destination: '',
    // Campos específicos para chamadas externas
    trunk_name_main: '',
    timeout_main: 60,
    // Campos específicos para DISA
    disa_context: '',
    disa_extension: 'menu',
    disa_priority: '1',
    recording_enabled: true,
    recording_format: 'wav',
    moh_enabled: false,
    moh_class: '',
    audio_enabled: false,
    audio_file: '',
    audio_type: 'ura',
    audio_accountcode: '',
    hangup_after_audio: false,
    description: '',
    active: true,
    // Configurações de Overflow
    overflows: [],
    // Configurações de Time-Conditions (GotoIfTime)
    gotoiftime_rules: []
  });

  const [errors, setErrors] = useState({});

  const history = useHistory();

  // Tipos de destino disponíveis. 'stasis' so aparece para usuario root
  // (backend bloqueia tambem; UI e apenas conveniencia).
  const destinationTypes = [
    { value: 'endpoint', label: 'Ramal', description: 'Encaminhar para um ramal específico' },
    { value: 'queue', label: 'Fila', description: 'Encaminhar para uma fila de atendimento' },
    { value: 'callcenter-v2', label: 'Callcenter V2', description: 'Fila inteligente via Stasis (CallcenterV2)' },
    { value: 'external', label: 'Externo', description: 'Encaminhar para número externo' },
    { value: 'disa', label: 'DISA', description: 'Acesso direto ao sistema' },
    { value: 'whatsapp', label: 'WhatsApp', description: 'Integração com WhatsApp' },
    { value: 'cycle', label: 'Ciclo', description: 'Reiniciar o fluxo do início' },
    { value: 'hangup', label: 'Desligar', description: 'Desligar a chamada' },
    ...(userRole === 'root' ? [
      { value: 'stasis', label: 'Agent IA', description: 'Encaminhar para um agente.' }
    ] : [])
  ];

  // Tipos de áudio
  const audioTypes = [
    { value: 'ura', label: 'URA' },
    { value: 'msg', label: 'Mensagem' },
    { value: 'moh', label: 'Música de Espera' }
  ];

  // Função para mostrar alert
  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

  // Carregar dados iniciais
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        const user = await accessLocalStorage.getUserLocalStorage();
        if (!user?.user?.idtenant) {
          console.error('❌ [INCOMING-CREATE] Tenant não encontrado');
          history.push('/config/chamadas-entrada');
          return;
        }

        const tenant = user.user.idtenant;
        setUserTenant(tenant);
        setUserRole(user.user.role || '');
        setValues(prev => ({ ...prev, tenant, audio_accountcode: tenant }));

        console.log('🔍 [INCOMING-CREATE] Carregando dados para tenant:', tenant);

        // Carregar dados em paralelo
        const [endpointsResult] = await Promise.all([
          fetchEndpoints()
        ]);

        if (endpointsResult?.success) {
          // Aplicar mesma lógica de filtro do MembersQueuesCreate
          let fetchedEndpoints = endpointsResult.data.map((element) => element).filter((element) => !isNaN(Number(element.endpoint)));
          fetchedEndpoints = tenant.includes('vgs') ? fetchedEndpoints : fetchedEndpoints.filter((endpoint) => tenant.includes(endpoint.tenant));
          
          setAvailableEndpoints(fetchedEndpoints);
          console.log('✅ [INCOMING-CREATE] Ramais carregados:', fetchedEndpoints.length);
        }

        // Carregar filas filtradas por tenant
        await fetchQueues(tenant);
        
        // Carregar contextos DISA
        await fetchContexts(tenant);
        
        // Carregar troncos disponíveis
        await fetchTrunks(tenant);
        
        // Verificar integrações WhatsApp
        await checkWhatsAppIntegration(tenant);

      } catch (error) {
        console.error('❌ [INCOMING-CREATE] Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [history]);

  // Limpar e configurar campos baseados no tipo de destino
  useEffect(() => {
    if (values.destination_type_main === 'hangup') {
      setValues(prev => ({ ...prev, destination_main: '' }));
    } else if (values.destination_type_main === 'whatsapp') {
      setValues(prev => ({ ...prev, destination_main: 'whatsapp' }));
    } else if (values.destination_type_main === 'stasis') {
      // stasis usa app fixo voice-gateway-ai — destino principal nao se aplica
      setValues(prev => ({ ...prev, destination_main: 'stasis' }));
    } else if (values.destination_type_main === 'disa' && availableContexts.length === 1) {
      // Se há apenas um contexto, selecionar automaticamente
      setValues(prev => ({ ...prev, disa_context: availableContexts[0].name }));
    }

    // Zerar fallback quando tipo nao for stasis (consistencia com CHECK do banco)
    if (values.destination_type_main !== 'stasis'
        && (values.stasis_fallback_type || values.stasis_fallback_destination)) {
      setValues(prev => ({ ...prev, stasis_fallback_type: '', stasis_fallback_destination: '' }));
    }
  }, [values.destination_type_main, availableContexts]);

  // Função para processar máscara (adicionar underscore se necessário)
  const processMask = (mask) => {
    if (!mask) return mask;
    
    const cleanMask = mask.trim();
    
    // Verificar se contém wildcards do Asterisk (N, X, Z) em maiúsculo
    const hasWildcards = /[NXZ]/.test(cleanMask);
    
    // Se tem wildcards e não começa com underscore, adicionar
    if (hasWildcards && !cleanMask.startsWith('_')) {
      return `_${cleanMask}`;
    }
    
    return cleanMask;
  };

  // Função para buscar e filtrar filas disponíveis
  const fetchQueues = async (idtenant) => {
    try {
      let queuesList = await exportSelectAllQueues();
      if (queuesList === undefined) return false;   
      
      // Filtrar filas por tenant se não for 'vgs'
      queuesList = idtenant.includes('vgs') ? queuesList : queuesList.filter((queue) => idtenant.includes(queue.accountcodequeue));
      
      // Manter o objeto completo das filas para ter name e description
      queuesList = queuesList ? queuesList.map((s) => ({
        name: s.namequeue,
        displayname: s.descqueue || 'Sem descrição'
      })) : [];
      
      setAvailableQueues(queuesList);
      console.log('✅ [INCOMING-CREATE] Filas filtradas carregadas:', queuesList.length);
      return true;
    } catch (error) {
      console.error('❌ [INCOMING-CREATE] Erro ao buscar filas:', error);
      setAvailableQueues([]);
      return false;
    }
  };

  // Função para buscar contextos DISA disponíveis
  const fetchContexts = async (tenant) => {
    try {
      console.log('🔍 [INCOMING-CREATE] Buscando contextos DISA para tenant:', tenant);
      
      const response = await getContextsByTenant(tenant);
      console.log('📦 [INCOMING-CREATE] Resposta de contextos:', response);
      
      // Padronizar extração de dados como nas outras telas
      let contexts = [];
      if (Array.isArray(response)) {
        contexts = response;
      } else if (response && response.success && Array.isArray(response.data)) {
        contexts = response.data;
      } else if (response && response.contexts) {
        contexts = response.contexts;
      }
      
      setAvailableContexts(contexts);
      console.log('✅ [INCOMING-CREATE] Contextos carregados:', contexts.length);
      
      // Se há apenas um contexto, selecionar automaticamente
      if (contexts.length === 1) {
        setValues(prev => ({ ...prev, disa_context: contexts[0].name }));
      }
    } catch (error) {
      console.error('❌ [INCOMING-CREATE] Erro ao buscar contextos:', error);
      setAvailableContexts([]);
    }
  };

  // Função para verificar se há integrações WhatsApp cadastradas
  const checkWhatsAppIntegration = async (tenant) => {
    try {
      console.log('🔍 [INCOMING-CREATE] Verificando integrações WhatsApp para tenant:', tenant);
      
      const response = await getIntegrationsByAccountcode(tenant);
      
      if (response && response.success && response.data && response.data.length > 0) {
        setHasWhatsAppIntegration(true);
        console.log('✅ [INCOMING-CREATE] Integrações WhatsApp encontradas:', response.data.length);
      } else {
        setHasWhatsAppIntegration(false);
        console.log('⚠️ [INCOMING-CREATE] Nenhuma integração WhatsApp encontrada');
      }
    } catch (error) {
      console.error('❌ [INCOMING-CREATE] Erro ao verificar integrações WhatsApp:', error);
      setHasWhatsAppIntegration(false);
    }
  };

  // Carregar troncos disponíveis
  const fetchTrunks = async (tenant) => {
    try {
      console.log('🔍 [INCOMING-CREATE] Buscando troncos para tenant:', tenant);
      
      const response = await getAvailableTrunks(tenant);
      
      if (response && response.success && Array.isArray(response.data)) {
        const filteredTrunks = response.data.filter(trunk => 
          trunk.accountcode === tenant || trunk.tenant === tenant
        );
        
        setAvailableTrunks(filteredTrunks);
        console.log('✅ [INCOMING-CREATE] Troncos carregados:', filteredTrunks.length);
      } else {
        setAvailableTrunks([]);
        console.log('⚠️ [INCOMING-CREATE] Nenhum tronco encontrado');
      }
    } catch (error) {
      console.error('❌ [INCOMING-CREATE] Erro ao buscar troncos:', error);
      setAvailableTrunks([]);
    }
  };

  // Manipular mudanças no formulário
  const handleInputChange = useCallback((field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  // Funções para gerenciar Overflows
  const addOverflow = () => {
    const newOverflow = {
      sequence_order: values.overflows.length + 1,
      timeout_seconds: 30,
      destination_overflow: '',
      destination_type: 'endpoint',
      audio_enabled: false,
      audio_file: '',
      audio_type: 'ura',
      audio_accountcode: userTenant,
      hangup_after_audio: false,
      active: true
    };
    
    setValues(prev => ({
      ...prev,
      overflows: [...prev.overflows, newOverflow]
    }));
  };

  const removeOverflow = (index) => {
    setValues(prev => ({
      ...prev,
      overflows: prev.overflows.filter((_, i) => i !== index)
    }));
  };

  const updateOverflow = (index, field, value) => {
    setValues(prev => ({
      ...prev,
      overflows: prev.overflows.map((overflow, i) => {
        if (i !== index) return overflow;
        
        const updatedOverflow = { ...overflow, [field]: value };
        
        // Se mudou o tipo de destino para WhatsApp, setar destination_overflow automaticamente
        if (field === 'destination_type' && value === 'whatsapp') {
          updatedOverflow.destination_overflow = 'whatsapp';
        }
        
        // Se mudou o tipo de destino para Ciclo, setar destination_overflow automaticamente
        if (field === 'destination_type' && value === 'cycle') {
          updatedOverflow.destination_overflow = values.mask || 'CYCLE_RESTART';
        }
        
        // Se mudou de cycle para outro tipo, limpar CYCLE_RESTART
        if (field === 'destination_type' && value !== 'cycle' && overflow.destination_overflow === 'CYCLE_RESTART') {
          updatedOverflow.destination_overflow = '';
        }
        
        return updatedOverflow;
      })
    }));
  };

  // Funções para gerenciar Time-Conditions (GotoIfTime)
  const addGotoIfTimeRule = () => {
    const newRule = {
      sequence_order: values.gotoiftime_rules.length + 1,
      time_range: '08:00-18:00',
      days_of_week: '1-5',
      days_of_month: '*',
      months: '*',
      destination: '',
      destination_type: 'context',
      context_name: '',
      extension_name: '',
      priority: 1,
      description: '',
      active: true
    };
    
    setValues(prev => ({
      ...prev,
      gotoiftime_rules: [...prev.gotoiftime_rules, newRule]
    }));
  };

  const removeGotoIfTimeRule = (index) => {
    setValues(prev => ({
      ...prev,
      gotoiftime_rules: prev.gotoiftime_rules.filter((_, i) => i !== index)
    }));
  };

  const updateGotoIfTimeRule = (index, field, value) => {
    setValues(prev => ({
      ...prev,
      gotoiftime_rules: prev.gotoiftime_rules.map((rule, i) => 
        i === index ? { ...rule, [field]: value } : rule
      )
    }));
  };

  // Validar formulário
  const validateForm = () => {
    const newErrors = {};

    if (!values.tenant?.trim()) {
      newErrors.tenant = 'Tenant é obrigatório';
    }

    if (!values.mask?.trim()) {
      newErrors.mask = 'Máscara de discagem é obrigatória';
    }

    if (!values.trunk_name?.trim()) {
      newErrors.trunk_name = 'Tronco é obrigatório para chamadas de entrada';
    }

    // Validações específicas por tipo de destino
    if (values.destination_type_main === 'endpoint') {
      if (values.destination_main?.trim()) {
        const ramal = values.destination_main.trim();
        if (!/^\d{4}$/.test(ramal)) {
          newErrors.destination_main = 'Ramal deve conter exatamente 4 dígitos numéricos';
        }
      }
    } else if (values.destination_type_main === 'external') {
      if (!values.destination_main?.trim()) {
        newErrors.destination_main = 'Número externo é obrigatório';
      }
      if (!values.trunk_name_main?.trim()) {
        newErrors.trunk_name_main = 'Tronco é obrigatório para chamadas externas';
      }
      if (!values.timeout_main || values.timeout_main < 1 || values.timeout_main > 300) {
        newErrors.timeout_main = 'Timeout deve estar entre 1 e 300 segundos';
      }
    } else if (values.destination_type_main === 'disa') {
      if (!values.disa_context?.trim()) {
        newErrors.disa_context = 'Contexto DISA é obrigatório';
      }
    } else if (values.destination_type_main === 'whatsapp') {
      if (!hasWhatsAppIntegration) {
        newErrors.destination_type_main = 'Nenhuma integração WhatsApp cadastrada para este tenant';
      }
    } else if (values.destination_type_main === 'stasis') {
      if (!values.stasis_fallback_type) {
        newErrors.stasis_fallback_type = 'Tipo de fallback obrigatorio para destino Stasis';
      } else if (values.stasis_fallback_type !== 'hangup' && !values.stasis_fallback_destination?.trim()) {
        newErrors.stasis_fallback_destination = 'Destino do fallback obrigatorio';
      }
    } else if (values.destination_type_main !== 'hangup' && !values.destination_main?.trim()) {
      newErrors.destination_main = 'Destino principal é obrigatório para este tipo de destino';
    }

    if (values.audio_enabled && !values.audio_file?.trim()) {
      newErrors.audio_file = 'Arquivo de áudio é obrigatório quando áudio está habilitado';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submeter formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      console.log('❌ [INCOMING-CREATE] Validação falhou:', errors);
      return;
    }

    try {
      setSaving(true);

      // Processar dados antes de enviar
      const processedData = { ...values };
      
      // Processar máscara
      processedData.mask = processMask(values.mask);
      
      // Processar destino principal baseado no tipo
      if (values.destination_type_main === 'external' && values.trunk_name_main) {
        processedData.destination_main = `${values.destination_main}@${values.trunk_name_main}`;
      } else if (values.destination_type_main === 'disa' && values.disa_context) {
        processedData.destination_main = `${values.disa_context},${values.disa_extension || 'menu'},${values.disa_priority || '1'}`;
      }
      
      // Processar overflows para concatenar campos compostos
      if (processedData.overflows && processedData.overflows.length > 0) {
        processedData.overflows = processedData.overflows.map(overflow => {
          const processedOverflow = { ...overflow };
          
          if (overflow.destination_type === 'external' && overflow.trunk_name) {
            processedOverflow.destination_overflow = `${overflow.destination_overflow}@${overflow.trunk_name}`;
          } else if (overflow.destination_type === 'disa' && overflow.disa_context) {
            processedOverflow.destination_overflow = `${overflow.disa_context},${overflow.disa_extension || 'menu'},${overflow.disa_priority || '1'}`;
          }
          
          return processedOverflow;
        });
      }

      const result = await createIncomingConfig(processedData);

      if (result?.success) {
        console.log('✅ [INCOMING-CREATE] Configuração criada com sucesso');
        showAlert('success', 'Configuração criada com sucesso!');
        setTimeout(() => {
          history.push('/config/chamadas-entrada');
        }, 2000);
      } else {
        console.error('❌ [INCOMING-CREATE] Erro ao criar:', result);
        showAlert('danger', result?.error || 'Erro ao criar configuração');
      }

    } catch (error) {
      console.error('❌ [INCOMING-CREATE] Erro na criação:', error);
      showAlert('danger', 'Erro interno. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  // Renderizar opções de destino baseado no tipo
  const renderDestinationOptions = () => {
    switch (values.destination_type_main) {
      case 'endpoint':
        return availableEndpoints.map(endpoint => (
          <option key={endpoint.endpoint} value={endpoint.endpoint}>
            {endpoint.endpoint}
          </option>
        ));
      
      case 'queue':
        return availableQueues.map(queue => (
          <option key={queue.name} value={queue.name}>
            {queue.name} - {queue.displayname || queue.name}
          </option>
        ));
      
      case 'disa':
        return availableContexts.filter(context => {
          const contextName = (context.name || '').toLowerCase();
          return contextName.includes('ura-') || contextName.includes('msg-');
        }).map(context => (
          <option key={context.name} value={context.name}>
            {context.label}
          </option>
        ));
      
      default:
        return null;
    }
  };

  // Retornar tipos de destino (ciclo só disponível para overflows)
  const getAvailableDestinationTypes = (isOverflow = false) => {
    if (isOverflow) {
      return destinationTypes; // Todos os tipos incluindo 'cycle'
    }
    // Para destino principal, remover 'cycle'
    return destinationTypes.filter(type => type.value !== 'cycle');
  };

  if (loading) return <Loading />;

  return (
    <div className="message-list-container">
      {/* Alert */}
      {alert.show && (
        <div className={`notification is-${alert.type}`}>
          <button className="delete" onClick={() => setAlert({ ...alert, show: false })}></button>
          <div style={{ whiteSpace: 'pre-line' }}>{alert.message}</div>
        </div>
      )}

       <div className="message-list-header">
         <div className="message-list-title-wrapper">
           <div className="message-list-title-left">
             <svg className="message-list-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
             </svg>
             <div>
               <h1 className="message-list-title">Nova Configuração de Entrada</h1>
               <p className="message-list-subtitle">Crie as regras e destinos para chamadas recebidas</p>
             </div>
           </div>
           <div className="message-list-actions">
             <button className="btn-new-message" style={{ background: '#e5e7eb', color: '#2c3e50' }} onClick={() => history.push('/config/chamadas-entrada')}>
               <FontAwesomeIcon icon={faArrowLeft} />
               Voltar
             </button>
           </div>
         </div>
       </div>

       <div className="columns is-centered">
         <div className="column is-8">
           <div className="box">

            <form onSubmit={handleSubmit}>
              {/* Informações Básicas */}
              <div className="field">
                <label className="label">
                  <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                  Cliente <span className="has-text-danger">*</span>
                </label>
                <div className="control">
                  <input 
                    className={`input ${errors.tenant ? 'is-danger' : ''}`}
                    type="text" 
                    value={values.tenant}
                    disabled
                  />
                </div>
                {errors.tenant && (
                  <p className="help is-danger">{errors.tenant}</p>
                )}
                <p className="help">Feixe do usuário logado (não editável)</p>
              </div>

              <div className="field">
                <label className="label">
                  <FontAwesomeIcon icon={faMask} className="mr-2" />
                  Máscara de Discagem (DID) *
                </label>
                <div className="control">
                  <input 
                    className={`input ${errors.mask ? 'is-danger' : ''}`}
                    type="text" 
                    placeholder="Ex: 1234, 11NXXXXXXX, 0800XXXXXXX"
                    value={values.mask}
                    onChange={(e) => {
                      const processedMask = processMask(e.target.value);
                      handleInputChange('mask', processedMask);
                    }}
                    onBlur={(e) => {
                      // Processar novamente no blur para garantir consistência
                      const processedMask = processMask(e.target.value);
                      if (processedMask !== e.target.value) {
                        handleInputChange('mask', processedMask);
                      }
                    }}
                  />
                </div>
                {errors.mask && <p className="help is-danger">{errors.mask}</p>}
                <p className="help">
                  <strong>Exemplos:</strong> 1234 (número fixo), 11NXXXXXXX (padrão), 0800XXXXXXX (0800)
                  <br />
                  <strong>Nota:</strong> Se usar X, N ou Z, o underscore (_) será adicionado automaticamente.
                </p>
              </div>

              {/* Seleção de Tronco */}
              <TrunkSelector
                selectedTrunk={values.trunk_name}
                onTrunkChange={(value) => handleInputChange('trunk_name', value)}
                tenant={userTenant}
                error={errors.trunk_name}
                required={true}
              />

              {/* Configuração de Destino Principal */}
              <div className="field">
                <label className="label">
                  {/* <FontAwesomeIcon icon={faRoute} className="mr-2" /> */}
                  Tipo de Destino Principal *
                </label>
                <div className="control">
                  <div className="select is-fullwidth">
                    <select
                      value={values.destination_type_main}
                      onChange={(e) => handleInputChange('destination_type_main', e.target.value)}
                    >
                      {getAvailableDestinationTypes(false).map(type => (
                        <option 
                          key={type.value} 
                          value={type.value}
                          disabled={type.value === 'whatsapp' && !hasWhatsAppIntegration}
                        >
                          {type.value === 'whatsapp' && !hasWhatsAppIntegration 
                            ? `${type.label} (sem integração)` 
                            : `${type.label} - ${type.description}`
                          }
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="field">
                <label className="label">
                  <FontAwesomeIcon icon={faPhone} className="mr-2" />
                  Destino Principal 
                  {values.destination_type_main !== 'endpoint' &&
                   values.destination_type_main !== 'hangup' &&
                   values.destination_type_main !== 'whatsapp' &&
                   values.destination_type_main !== 'stasis' &&
                   <span className="has-text-danger">*</span>}
                  {(values.destination_type_main === 'endpoint' ||
                    values.destination_type_main === 'hangup' ||
                    values.destination_type_main === 'whatsapp' ||
                    values.destination_type_main === 'stasis') &&
                   <span className="has-text-grey">(Automático)</span>}
                </label>
                <div className="control">
                  {values.destination_type_main === 'hangup' ? (
                    <input
                      className="input"
                      type="text"
                      placeholder="Chamada será desligada (sem destino)"
                      value=""
                      disabled
                    />
                  ) : values.destination_type_main === 'whatsapp' ? (
                    <input
                      className="input"
                      type="text"
                      placeholder="Encaminhamento automático para WhatsApp"
                      value="WhatsApp AGI"
                      disabled
                    />
                  ) : values.destination_type_main === 'stasis' ? (
                    <input
                      className="input"
                      type="text"
                      placeholder="Encaminhamento para Stasis voice-gateway-ai"
                      value="App Stasis (voice-gateway-ai)"
                      disabled
                    />
                  ) : values.destination_type_main === 'endpoint' ? (
                    <input 
                      className={`input ${errors.destination_main ? 'is-danger' : ''}`}
                      type="text" 
                      placeholder="Ex: 1000, 1001, 1234"
                      value={values.destination_main}
                      onChange={(e) => {
                        // Permitir apenas números e limitar a 4 dígitos
                        const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                        handleInputChange('destination_main', value);
                      }}
                      maxLength={4}
                    />
                  ) : values.destination_type_main === 'queue' ? (
                    <div className="select is-fullwidth">
                      <select
                        className={errors.destination_main ? 'is-danger' : ''}
                        value={values.destination_main}
                        onChange={(e) => handleInputChange('destination_main', e.target.value)}
                      >
                        <option value="">Selecione uma fila</option>
                        {renderDestinationOptions()}
                      </select>
                    </div>
                  ) : values.destination_type_main === 'disa' ? (
                    <div className="select is-fullwidth">
                      <select
                        className={errors.disa_context ? 'is-danger' : ''}
                        value={values.disa_context}
                        onChange={(e) => handleInputChange('disa_context', e.target.value)}
                      >
                        <option value="">Selecione um contexto</option>
                        {renderDestinationOptions()}
                      </select>
                    </div>
                  ) : values.destination_type_main === 'callcenter-v2' ? (
                    <input
                      className={`input ${errors.destination_main ? 'is-danger' : ''}`}
                      type="text"
                      placeholder="Ex: suporte-mlz, vendas-mlz"
                      value={values.destination_main}
                      onChange={(e) => handleInputChange('destination_main', e.target.value)}
                    />
                  ) : values.destination_type_main === 'external' ? (
                    // Campo de número externo (será combinado com o tronco posteriormente)
                    <input
                      className={`input ${errors.destination_main ? 'is-danger' : ''}`}
                      type="text"
                      placeholder="Ex: 1134567890"
                      value={values.destination_main}
                      onChange={(e) => handleInputChange('destination_main', e.target.value)}
                    />
                  ) : (
                    <input 
                      className={`input ${errors.destination_main ? 'is-danger' : ''}`}
                      type="text" 
                      placeholder="Destino da chamada"
                      value={values.destination_main}
                      onChange={(e) => handleInputChange('destination_main', e.target.value)}
                    />
                  )}
                </div>
                {errors.destination_main && <p className="help is-danger">{errors.destination_main}</p>}
                {errors.disa_context && <p className="help is-danger">{errors.disa_context}</p>}
                
                {/* Mensagem de ajuda específica para cada tipo */}
                {values.destination_type_main === 'endpoint' && (
                  <p className="help">
                    Digite um ramal de 4 dígitos numéricos (ex: 1000, 1001, 1234). 
                    Se deixar vazio, será usada a máscara de discagem.
                  </p>
                )}
                {values.destination_type_main === 'queue' && (
                  <p className="help">Selecione a fila para onde a chamada será direcionada.</p>
                )}
                {values.destination_type_main === 'callcenter-v2' && (
                  <p className="help">Digite o nome da fila do Callcenter V2 (ex: suporte-mlz, vendas-mlz). A chamada sera roteada via Stasis.</p>
                )}
                {values.destination_type_main === 'external' && (
                  <p className="help">Digite o número externo completo (ex: 1134567890).</p>
                )}
                {values.destination_type_main === 'disa' && (
                  <p className="help">
                    Selecione o contexto DISA para acesso direto ao sistema. 
                    {availableContexts.length === 0 && <span className="has-text-warning"> Nenhum contexto disponível.</span>}
                  </p>
                )}
                {values.destination_type_main === 'whatsapp' && (
                  <p className="help">
                    🟢 A chamada será encaminhada automaticamente para WhatsApp via AGI. 
                    {!hasWhatsAppIntegration && <span className="has-text-danger"> Nenhuma integração WhatsApp cadastrada.</span>}
                  </p>
                )}
                {values.destination_type_main === 'hangup' && (
                  <p className="help">A chamada será desligada automaticamente (sem destino).</p>
                )}
                {values.destination_type_main === 'stasis' && (
                  <p className="help">
                    A chamada sera entregue ao app Stasis voice-gateway-ai (IA). Configure abaixo
                    o destino de fallback para casos em que o app esteja indisponivel.
                  </p>
                )}
              </div>

              {/* Inputs de fallback do Stasis */}
              {values.destination_type_main === 'stasis' && (
                <StasisFallbackInputs
                  fallbackType={values.stasis_fallback_type}
                  fallbackDestination={values.stasis_fallback_destination}
                  onChange={handleInputChange}
                  availableEndpoints={availableEndpoints}
                  availableQueues={availableQueues}
                  availableContexts={availableContexts}
                  errorType={errors.stasis_fallback_type}
                  errorDestination={errors.stasis_fallback_destination}
                />
              )}

              {/* Campo Timeout para endpoint, queue e external */}
              {(values.destination_type_main === 'endpoint' || 
                values.destination_type_main === 'queue' || 
                values.destination_type_main === 'external') && (
                <div className="field">
                  <label className="label">
                    <FontAwesomeIcon icon={faClock} className="mr-2" />
                    Timeout (segundos) {values.destination_type_main !== 'queue' && '*'}
                  </label>
                  <div className="control">
                    <input 
                      className={`input ${errors.timeout_main ? 'is-danger' : ''}`}
                      type="number" 
                      min="1"
                      max="300"
                      placeholder={
                        values.destination_type_main === 'queue' 
                          ? (values.overflows && values.overflows.length > 0 ? '15' : 'Sem timeout (indefinido)')
                          : (values.overflows && values.overflows.length > 0 ? '20' : '60')
                      }
                      value={values.timeout_main}
                      onChange={(e) => handleInputChange('timeout_main', parseInt(e.target.value) || 60)}
                    />
                  </div>
                  {errors.timeout_main && <p className="help is-danger">{errors.timeout_main}</p>}
                  {values.destination_type_main === 'endpoint' && (
                    <p className="help">Tempo limite de toque para o ramal (1-300 segundos).</p>
                  )}
                  {values.destination_type_main === 'queue' && (
                    <p className="help">
                      Tempo máximo na fila antes do overflow. 
                      {values.overflows && values.overflows.length > 0 
                        ? ' Padrão: 15 segundos.' 
                        : ' Deixe vazio para aguardar indefinidamente até um atendente responder.'}
                    </p>
                  )}
                  {values.destination_type_main === 'external' && (
                    <p className="help">Tempo limite para a chamada externa (1-300 segundos).</p>
                  )}
                </div>
              )}

              {/* Campos específicos para chamadas externas */}
              {values.destination_type_main === 'external' && (
                <div className="field">
                  <label className="label">
                    <FontAwesomeIcon icon={faServer} className="mr-2" />
                    Tronco *
                  </label>
                  <div className="control">
                    <div className="select is-fullwidth">
                      <select
                        value={values.trunk_name_main}
                        onChange={(e) => handleInputChange('trunk_name_main', e.target.value)}
                        className={errors.trunk_name_main ? 'is-danger' : ''}
                      >
                        <option value="">Selecione o tronco</option>
                        {availableTrunks.map(trunk => (
                          <option key={trunk.name} value={trunk.name}>
                            {trunk.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {errors.trunk_name_main && <p className="help is-danger">{errors.trunk_name_main}</p>}
                  <p className="help">Tronco utilizado para realizar a chamada externa.</p>
                </div>
              )}

              {/* Configurações de Gravação e Áudio */}
              <div className="columns">
                <div className="column is-6">
                  <div className="field">
                    <label className="label">
                      <FontAwesomeIcon icon={faRecordVinyl} className="mr-2" />
                      Gravação de Chamadas
                    </label>
                    <div className="control">
                      <label className="checkbox">
                        <input 
                          type="checkbox"
                          checked={values.recording_enabled}
                          onChange={(e) => handleInputChange('recording_enabled', e.target.checked)}
                        />
                        <span className="ml-2">Habilitar gravação</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="column is-6">
                  <div className="field">
                    <label className="label">
                      <FontAwesomeIcon icon={faVolumeUp} className="mr-2" />
                      Reprodução de Áudio
                    </label>
                    <div className="control">
                      <label className="checkbox">
                        <input 
                          type="checkbox"
                          checked={values.audio_enabled}
                          onChange={(e) => handleInputChange('audio_enabled', e.target.checked)}
                        />
                        <span className="ml-2">Reproduzir áudio antes do encaminhamento</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Configuração de Music on Hold */}
              <div className="columns mt-4">
                <div className="column">
                  <MohConfiguration
                    config={{
                      moh_enabled: values.moh_enabled || false,
                      moh_class: values.moh_class || ''
                    }}
                    tenant={userTenant}
                    onChange={(mohConfig) => {
                      handleInputChange('moh_enabled', mohConfig.moh_enabled);
                      handleInputChange('moh_class', mohConfig.moh_class);
                    }}
                    disabled={saving}
                  />
                </div>
              </div>

              {values.audio_enabled && (
                <div className="field">
                  <label className="label">Arquivo de Áudio *</label>
                  <AudioSelectorUploads
                    tenant={userTenant}
                    selectedFile={values.audio_file}
                    selectedType={values.audio_type}
                    onAudioChange={(audioData) => {
                      handleInputChange('audio_file', audioData.file);
                      handleInputChange('audio_type', audioData.type);
                      handleInputChange('audio_accountcode', audioData.accountcode);
                    }}
                    error={errors.audio_file}
                  />
                  {errors.audio_file && <p className="help is-danger">{errors.audio_file}</p>}
                  
                  <div className="field mt-4">
                    <label className="checkbox">
                      <input 
                        type="checkbox"
                        checked={values.hangup_after_audio}
                        onChange={(e) => handleInputChange('hangup_after_audio', e.target.checked)}
                      />
                      <span className="ml-2">Desligar após reproduzir áudio</span>
                    </label>
                    <p className="help">Útil para mensagens informativas (URA passiva)</p>
                  </div>
                </div>
              )}

              {/* Configurações de Time-Conditions */}
              <div className="box mt-5">
                <div className="level">
                  <div className="level-left">
                    <div className="level-item">
                      <h2 className="title is-5">
                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                        Desvios por Data/Hora
                      </h2>
                    </div>
                  </div>
                  <div className="level-right">
                    <div className="level-item">
                      <button
                        type="button"
                        className="button is-info is-small"
                        onClick={addGotoIfTimeRule}
                      >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Adicionar Regra
                      </button>
                    </div>
                  </div>
                </div>

                {values.gotoiftime_rules.length === 0 ? (
                  <div className="notification is-light">
                    <p className="has-text-centered">
                      <strong>Nenhuma condição de tempo configurada</strong>
                    </p>
                    <p className="has-text-centered">
                      Adicione regras para desviar chamadas baseadas em horário.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Exemplos de Configuração */}
                    <div className="notification is-info is-light mb-4">
                      <h4 className="subtitle is-6">
                        📚 Exemplos de Configuração
                      </h4>
                      <div className="table-container">
                        <table className="table is-fullwidth is-striped is-narrow">
                          <thead>
                            <tr>
                              <th>Descrição</th>
                              <th>Horário</th>
                              <th>Dias Semana</th>
                              <th>Dias Mês</th>
                              <th>Meses</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>Comercial (Seg-Sex 8h-18h)</td>
                              <td><code>08:00-17:59</code></td>
                              <td><code>mon-fri</code></td>
                              <td><code>*</code></td>
                              <td><code>*</code></td>
                            </tr>
                            <tr>
                              <td>Fim de semana</td>
                              <td><code>*</code></td>
                              <td><code>sat-sun</code></td>
                              <td><code>*</code></td>
                              <td><code>*</code></td>
                            </tr>
                            <tr>
                              <td>Feriados específicos</td>
                              <td><code>*</code></td>
                              <td><code>*</code></td>
                              <td><code>1,25</code></td>
                              <td><code>jan,dec</code></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {values.gotoiftime_rules.map((rule, index) => (
                    <div key={index} className="box">
                      <div className="level">
                        <div className="level-left">
                          <div className="level-item">
                            <h3 className="subtitle is-6">Regra #{index + 1}</h3>
                          </div>
                        </div>
                        <div className="level-right">
                          <div className="level-item">
                            <button
                              type="button"
                              className="button is-small is-danger"
                              onClick={() => removeGotoIfTimeRule(index)}
                              title="Remover regra"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="columns">
                        <div className="column is-3">
                          <div className="field">
                            <label className="label is-small">Horário</label>
                            <div className="control">
                              <input
                                className="input is-small"
                                type="text"
                                placeholder="08:00-18:00"
                                value={rule.time_range}
                                onChange={(e) => updateGotoIfTimeRule(index, 'time_range', e.target.value)}
                              />
                            </div>
                            <p className="help is-size-7">Formato: HH:MM-HH:MM</p>
                          </div>
                        </div>

                        <div className="column is-2">
                          <div className="field">
                            <label className="label is-small">Dias da Semana</label>
                            <div className="control">
                              <input
                                className="input is-small"
                                type="text"
                                placeholder="1-5"
                                value={rule.days_of_week}
                                onChange={(e) => updateGotoIfTimeRule(index, 'days_of_week', e.target.value)}
                              />
                            </div>
                            <p className="help is-size-7">1=Dom, 2=Seg, etc</p>
                          </div>
                        </div>

                        <div className="column is-2">
                          <div className="field">
                            <label className="label is-small">Dias do Mês</label>
                            <div className="control">
                              <input
                                className="input is-small"
                                type="text"
                                placeholder="*"
                                value={rule.days_of_month}
                                onChange={(e) => updateGotoIfTimeRule(index, 'days_of_month', e.target.value)}
                              />
                            </div>
                            <p className="help is-size-7">* = todos</p>
                          </div>
                        </div>

                        <div className="column is-2">
                          <div className="field">
                            <label className="label is-small">Meses</label>
                            <div className="control">
                              <input
                                className="input is-small"
                                type="text"
                                placeholder="*"
                                value={rule.months}
                                onChange={(e) => updateGotoIfTimeRule(index, 'months', e.target.value)}
                              />
                            </div>
                            <p className="help is-size-7">* = todos</p>
                          </div>
                        </div>

                        <div className="column is-3">
                          <div className="field">
                            <label className="label is-small">Destino</label>
                            <div className="control">
                              <input
                                className="input is-small"
                                type="text"
                                placeholder="Ex: fora-expediente,s,1"
                                value={rule.destination}
                                onChange={(e) => updateGotoIfTimeRule(index, 'destination', e.target.value)}
                              />
                            </div>
                            <p className="help is-size-7">Context,extension,priority</p>
                          </div>
                        </div>
                      </div>

                      <div className="field">
                        <label className="label is-small">Descrição</label>
                        <div className="control">
                          <input
                            className="input is-small"
                            type="text"
                            placeholder="Ex: Fora do horário comercial"
                            value={rule.description}
                            onChange={(e) => updateGotoIfTimeRule(index, 'description', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  </>
                )}
              </div>

              {/* Configurações de Overflow */}
              <div className="box mt-5">
                <div className="level">
                  <div className="level-left">
                    <div className="level-item">
                      <h2 className="title is-5">
                        <FontAwesomeIcon icon={faExchangeAlt} className="mr-2" />
                        Configurações de Transbordo
                      </h2>
                    </div>
                  </div>
                  <div className="level-right">
                    <div className="level-item">
                      <button
                        type="button"
                        className="button is-primary is-small"
                        onClick={addOverflow}
                      >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Adicionar Transbordo
                      </button>
                    </div>
                  </div>
                </div>

                {values.overflows.length === 0 ? (
                  <div className="notification is-light">
                    <p className="has-text-centered">
                      <strong>Nenhum transbordo configurado</strong>
                    </p>
                    <p className="has-text-centered">
                      Clique em "Adicionar Transbordo" para criar uma cadeia de encaminhamentos.
                    </p>
                  </div>
                ) : (
                  values.overflows.map((overflow, index) => (
                    <div key={index} className="box">
                      <div className="level">
                        <div className="level-left">
                          <div className="level-item">
                            <h3 className="subtitle is-6">Transbordo #{index + 1}</h3>
                          </div>
                        </div>
                        <div className="level-right">
                          <div className="level-item">
                            <button
                              type="button"
                              className="button is-small is-danger"
                              onClick={() => removeOverflow(index)}
                              title="Remover transbordo"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="columns">
                        <div className="column is-3">
                          <div className="field">
                            <label className="label is-small">Timeout (segundos)</label>
                            <div className="control">
                              <input
                                className="input is-small"
                                type="number"
                                min="5"
                                max="300"
                                value={overflow.timeout_seconds}
                                onChange={(e) => updateOverflow(index, 'timeout_seconds', parseInt(e.target.value))}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="column is-3">
                          <div className="field">
                            <label className="label is-small">Tipo de Destino</label>
                            <div className="control">
                              <div className="select is-small is-fullwidth">
                                <select
                                  value={overflow.destination_type}
                                  onChange={(e) => updateOverflow(index, 'destination_type', e.target.value)}
                                >
                                  {getAvailableDestinationTypes(true).map(type => (
                                    <option 
                                      key={type.value} 
                                      value={type.value}
                                      disabled={type.value === 'whatsapp' && !hasWhatsAppIntegration}
                                    >
                                      {type.value === 'whatsapp' && !hasWhatsAppIntegration 
                                        ? `${type.label} (sem integração)` 
                                        : type.label
                                      }
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="column is-6">
                          <div className="field">
                            <label className="label is-small">Destino</label>
                            <div className="control">
                              {overflow.destination_type === 'hangup' ? (
                                <input
                                  className="input is-small"
                                  type="text"
                                  placeholder="Desligar chamada"
                                  value=""
                                  disabled
                                />
                              ) : overflow.destination_type === 'whatsapp' ? (
                                <input
                                  className="input is-small"
                                  type="text"
                                  placeholder="WhatsApp AGI"
                                  value="WhatsApp AGI"
                                  disabled
                                />
                              ) : overflow.destination_type === 'cycle' ? (
                                <input
                                  className="input is-small"
                                  type="text"
                                  placeholder="Reiniciar fluxo"
                                  value={overflow.destination_overflow || values.mask || 'CYCLE_RESTART'}
                                  disabled
                                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                                />
                              ) : overflow.destination_type === 'endpoint' ? (
                                <input
                                  className="input is-small"
                                  type="text"
                                  placeholder="Ex: 1000, 1001"
                                  value={overflow.destination_overflow}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                                    updateOverflow(index, 'destination_overflow', value);
                                  }}
                                  maxLength={4}
                                />
                              ) : ['queue'].includes(overflow.destination_type) ? (
                                <div className="select is-small is-fullwidth">
                                  <select
                                    value={overflow.destination_overflow}
                                    onChange={(e) => updateOverflow(index, 'destination_overflow', e.target.value)}
                                  >
                                    <option value="">Selecione uma fila</option>
                                    {availableQueues.map(queue => (
                                      <option key={queue.name} value={queue.name}>
                                        {queue.name} - {queue.displayname || queue.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              ) : overflow.destination_type === 'disa' ? (
                                <div className="select is-small is-fullwidth">
                                  <select
                                    value={overflow.disa_context || ''}
                                    onChange={(e) => updateOverflow(index, 'disa_context', e.target.value)}
                                  >
                                    <option value="">Selecione um contexto</option>
                                    {availableContexts.filter(context => {
                                      const contextName = (context.name || '').toLowerCase();
                                      return contextName.includes('ura-') || contextName.includes('msg-');
                                    }).map(context => (
                                      <option key={context.name} value={context.name}>
                                        {context.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              ) : overflow.destination_type === 'external' ? (
                                <>
                                  <input
                                    className="input is-small"
                                    type="text"
                                    placeholder="Ex: 1134567890"
                                    value={overflow.destination_overflow}
                                    onChange={(e) => updateOverflow(index, 'destination_overflow', e.target.value)}
                                  />
                                  <div className="mt-2">
                                    <label className="label is-small">Tronco *</label>
                                    <div className="select is-small is-fullwidth">
                                      <select
                                        value={overflow.trunk_name || ''}
                                        onChange={(e) => updateOverflow(index, 'trunk_name', e.target.value)}
                                      >
                                        <option value="">Selecione o tronco</option>
                                        {availableTrunks.map(trunk => (
                                          <option key={trunk.name} value={trunk.name}>
                                            {trunk.name}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                </>
                              ) : overflow.destination_type === 'stasis' ? (
                                <input
                                  className="input is-small"
                                  type="text"
                                  placeholder="App Stasis voice-gateway-ai"
                                  value="voice-gateway-ai (fixo)"
                                  disabled
                                />
                              ) : (
                                <input
                                  className="input is-small"
                                  type="text"
                                  placeholder="Destino do transbordo"
                                  value={overflow.destination_overflow}
                                  onChange={(e) => updateOverflow(index, 'destination_overflow', e.target.value)}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Inputs de fallback do Stasis para o overflow */}
                      {overflow.destination_type === 'stasis' && (
                        <StasisFallbackInputs
                          fallbackType={overflow.stasis_fallback_type || ''}
                          fallbackDestination={overflow.stasis_fallback_destination || ''}
                          onChange={(field, value) => updateOverflow(index, field, value)}
                          availableEndpoints={availableEndpoints}
                          availableQueues={availableQueues}
                          availableContexts={availableContexts}
                        />
                      )}

                      {/* Configurações de áudio para overflow */}
                      <div className="field">
                        <label className="checkbox">
                          <input
                            type="checkbox"
                            checked={overflow.audio_enabled}
                            onChange={(e) => updateOverflow(index, 'audio_enabled', e.target.checked)}
                          />
                          <span className="ml-2">Reproduzir áudio antes deste transbordo</span>
                        </label>
                      </div>

                      {overflow.audio_enabled && (
                        <div className="field">
                          <label className="label is-small">Arquivo de Áudio</label>
                          <AudioSelectorUploads
                            tenant={userTenant}
                            selectedFile={overflow.audio_file}
                            selectedType={overflow.audio_type}
                            onAudioChange={(audioData) => {
                              updateOverflow(index, 'audio_file', audioData.file);
                              updateOverflow(index, 'audio_type', audioData.type);
                              updateOverflow(index, 'audio_accountcode', audioData.accountcode);
                            }}
                            size="small"
                          />
                          
                          <div className="field mt-3">
                            <label className="checkbox">
                              <input
                                type="checkbox"
                                checked={overflow.hangup_after_audio}
                                onChange={(e) => updateOverflow(index, 'hangup_after_audio', e.target.checked)}
                              />
                              <span className="ml-2">Desligar após áudio</span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Descrição */}
              <div className="field">
                <label className="label">Descrição</label>
                <div className="control">
                  <textarea 
                    className="textarea" 
                    placeholder="Descrição opcional da configuração"
                    value={values.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows="3"
                  />
                </div>
              </div>

              {/* Status */}
              <div className="field">
                <label className="label">Status</label>
                <div className="control">
                  <label className="checkbox">
                    <input 
                      type="checkbox"
                      checked={values.active}
                      onChange={(e) => handleInputChange('active', e.target.checked)}
                    />
                    <span className="ml-2">
                      <FontAwesomeIcon 
                        icon={values.active ? faToggleOn : faToggleOff} 
                        className={`mr-1 ${values.active ? 'has-text-success' : 'has-text-grey'}`}
                      />
                      Configuração ativa
                    </span>
                  </label>
                </div>
              </div>

              {/* Erro de submissão */}
              {errors.submit && (
                <div className="notification is-danger">
                  <strong>Erro:</strong> {errors.submit}
                </div>
              )}

              {/* Botões de ação */}
              <div className="field is-grouped is-grouped-right">
                <div className="control">
                  <button 
                    type="button"
                    className="button is-light"
                    onClick={() => history.push('/config/chamadas-entrada')}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                    Cancelar
                  </button>
                </div>
                <div className="control">
                  <button 
                    type="submit"
                    className={`button is-primary ${saving ? 'is-loading' : ''}`}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    Salvar Configuração
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IncomingCallsCreate;
